import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { HiMenu, HiX } from 'react-icons/hi';
import { motion, AnimatePresence } from 'framer-motion';

export const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const closeMenu = () => setIsMenuOpen(false);

  return (
    <header className="fixed top-6 right-6 xl:top-8 xl:right-8 2xl:top-10 2xl:right-10 z-50">
      <nav className="bg-white/10 dark:bg-gray-900/20 backdrop-blur-xl border border-white/20 dark:border-gray-700/30 rounded-3xl shadow-lg overflow-hidden">
        {/* Header Top Section */}
        <div className="flex items-center justify-between gap-x-2 md:gap-x-6 xl:gap-x-8 2xl:gap-x-10 pl-1 pr-3 md:px-6 xl:px-8 2xl:px-10 py-3">
          <div className="flex items-center space-x-4 sm:space-x-6 xl:space-x-8 2xl:space-x-10">
            {/* Logo / Title - Always visible on desktop, conditional on mobile based on scroll */}
            {/* Desktop: Always show */}
            <Link
              to="/"
              onClick={closeMenu}
              className="hidden md:block font-display font-bold text-sm sm:text-base xl:text-lg 2xl:text-xl text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors duration-200 whitespace-nowrap"
            >
              Moritz
            </Link>

            {/* Mobile: Show only when menu is open */}
            <AnimatePresence mode="wait" initial={false}>
              {isMenuOpen && (
                <motion.div
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -10 }}
                  transition={{ duration: 0.2 }}
                  className="md:hidden"
                >
                  <Link
                    to="/"
                    onClick={closeMenu}
                    className="font-display font-bold text-base xl:text-lg 2xl:text-xl text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors duration-200 whitespace-nowrap"
                  >
                    Moritz
                  </Link>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Desktop Navigation Links */}
            <div className="hidden md:flex items-center space-x-3 sm:space-x-4 xl:space-x-6 2xl:space-x-8">
              <Link
                to="/ueber-mich"
                className="text-xs sm:text-sm xl:text-base 2xl:text-lg text-gray-700 dark:text-gray-200 hover:text-primary-600 dark:hover:text-primary-400 font-medium transition-colors duration-200 whitespace-nowrap"
              >
                Über mich
              </Link>
              <Link
                to="/projekte"
                className="text-xs sm:text-sm xl:text-base 2xl:text-lg text-gray-700 dark:text-gray-200 hover:text-primary-600 dark:hover:text-primary-400 font-medium transition-colors duration-200 whitespace-nowrap"
              >
                Projekte
              </Link>
              <Link
                to="/webinare"
                className="text-xs sm:text-sm xl:text-base 2xl:text-lg text-gray-700 dark:text-gray-200 hover:text-primary-600 dark:hover:text-primary-400 font-medium transition-colors duration-200 whitespace-nowrap"
              >
                Webinare
              </Link>
            </div>

          </div>

          {/* Mobile Hamburger/Close Button */}
          <motion.button
            onClick={toggleMenu}
            className="md:hidden hover:scale-110 active:scale-95 transition-transform duration-200 ease-in-out p-1"
            aria-label={isMenuOpen ? 'Menü schließen' : 'Menü öffnen'}
            whileTap={{ scale: 0.95 }}
          >
            <AnimatePresence mode="wait" initial={false}>
              {isMenuOpen ? (
                <motion.div
                  key="close"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.1 }}
                >
                  <HiX className="w-6 h-6 xl:w-7 xl:h-7 2xl:w-8 2xl:h-8 text-primary-600 dark:text-primary-400" />
                </motion.div>
              ) : (
                <motion.div
                  key="menu"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.1 }}
                >
                  <HiMenu className="w-6 h-6 xl:w-7 xl:h-7 2xl:w-8 2xl:h-8 text-primary-600 dark:text-primary-400" />
                </motion.div>
              )}
            </AnimatePresence>
          </motion.button>
        </div>

        {/* Mobile Dropdown Menu */}
        <AnimatePresence initial={false}>
          {isMenuOpen && (
            <motion.div
              initial={{ gridTemplateRows: '0fr', opacity: 0 }}
              animate={{ gridTemplateRows: '1fr', opacity: 1 }}
              exit={{ gridTemplateRows: '0fr', opacity: 0 }}
              transition={{
                duration: 0.2,
                ease: 'easeOut'
              }}
              className="md:hidden grid border-t border-white/20 dark:border-gray-700/30"
            >
              <div className="overflow-hidden" style={{ minHeight: 0 }}>
                <div className="px-6 py-4 space-y-3">
                  {/* Navigation Links */}
                  <Link
                    to="/"
                    onClick={closeMenu}
                    className="block py-2 text-sm xl:text-base 2xl:text-lg font-medium text-gray-700 dark:text-gray-200 hover:text-primary-600 dark:hover:text-primary-400 transition-colors duration-200"
                  >
                    Home
                  </Link>

                  <Link
                    to="/ueber-mich"
                    onClick={closeMenu}
                    className="block py-2 text-sm xl:text-base 2xl:text-lg font-medium text-gray-700 dark:text-gray-200 hover:text-primary-600 dark:hover:text-primary-400 transition-colors duration-200"
                  >
                    Über mich
                  </Link>

                  <Link
                    to="/projekte"
                    onClick={closeMenu}
                    className="block py-2 text-sm xl:text-base 2xl:text-lg font-medium text-gray-700 dark:text-gray-200 hover:text-primary-600 dark:hover:text-primary-400 transition-colors duration-200"
                  >
                    Projekte
                  </Link>

                  <Link
                    to="/webinare"
                    onClick={closeMenu}
                    className="block py-2 text-sm xl:text-base 2xl:text-lg font-medium text-gray-700 dark:text-gray-200 hover:text-primary-600 dark:hover:text-primary-400 transition-colors duration-200"
                  >
                    Webinare
                  </Link>

                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </header>
  );
};
